import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus, Check, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

const HEADER_IMG = "/images/vauxhall-wide.jpg";

function ServiceRow({ service, isOpen, onToggle }) {
  const Icon = service.icon;
  return (
    <div className="border-b border-[#27272A]" data-testid={`service-row-${service.id}`}>
      <button
        onClick={onToggle}
        data-testid={`service-toggle-${service.id}`}
        className="w-full flex items-center gap-5 py-8 text-left group"
        aria-expanded={isOpen}
      >
        <Icon size={30} strokeWidth={1} className={`shrink-0 transition-colors ${isOpen ? "text-[#C9A24B]" : "text-[#A1A1AA] group-hover:text-[#C9A24B]"}`} />
        <div className="flex-1 min-w-0">
          <h3 className={`font-serif text-2xl sm:text-3xl transition-colors ${isOpen ? "text-[#C9A24B]" : "text-[#F5F5F0] group-hover:text-[#C9A24B]"}`}>
            {service.title}
          </h3>
          {!isOpen && (
            <p className="mt-2 text-[#A1A1AA] font-light hidden sm:block line-clamp-1">{service.summary}</p>
          )}
        </div>
        <span className={`shrink-0 flex items-center justify-center w-10 h-10 rounded-full border transition-colors ${isOpen ? "border-[#C9A24B] text-[#C9A24B]" : "border-[#27272A] text-[#A1A1AA] group-hover:border-[#C9A24B] group-hover:text-[#C9A24B]"}`}>
          {isOpen ? <Minus size={18} strokeWidth={1.5} /> : <Plus size={18} strokeWidth={1.5} />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-10 pb-12 pl-0 sm:pl-14">
              <div>
                <p className="text-[#A1A1AA] font-light leading-relaxed text-lg">{service.summary}</p>
                <p className="mt-4 text-[#A1A1AA] font-light leading-relaxed">{service.description}</p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                  {service.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[#D4D4D8] font-light">
                      <Check size={16} strokeWidth={1.5} className="text-[#C9A24B] mt-1 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-64 lg:h-auto overflow-hidden order-first lg:order-last">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/60 to-transparent" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Services() {
  const [openId, setOpenId] = useState(SERVICES[0].id);

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="relative pt-40 pb-24 grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HEADER_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-[#0A0A0B]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <SectionLabel className="mb-6">Our expertise</SectionLabel>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F0] max-w-3xl">
              Services
            </h1>
            <p className="mt-6 text-lg text-[#A1A1AA] font-light max-w-2xl">
              A spectrum of discreet, intelligence-led services. Select any service below to reveal its
              full scope and capabilities.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Accordion */}
      <section className="pb-24 bg-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="border-t border-[#27272A]">
            {SERVICES.map((s) => (
              <ServiceRow
                key={s.id}
                service={s}
                isOpen={openId === s.id}
                onToggle={() => setOpenId(openId === s.id ? null : s.id)}
              />
            ))}
          </div>

          <Reveal>
            <div className="mt-16 text-center bg-[#121214] border border-[#27272A] p-12">
              <p className="text-[#A1A1AA] font-light text-lg max-w-2xl mx-auto">
                Our capabilities extend beyond those listed here. Many mandates are bespoke and discussed
                in strict confidence — we would be pleased to explore how we can support you.
              </p>
              <Link
                to="/contact"
                data-testid="services-contact-cta"
                className="group mt-8 inline-flex items-center gap-2 bg-[#C9A24B] text-black px-8 py-4 text-sm tracking-[0.12em] uppercase hover:bg-[#E5E4E2] transition-colors duration-300"
              >
                Request a consultation
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
